import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, FileText, Building2,
  Briefcase, Layers, HelpCircle, Wallet, ListTree, Target, Info,
  Receipt, ListOrdered, Repeat, TrendingUp, Check, ChevronDown
} from 'lucide-react';
import { incomingService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import './IncomingAccountsPayablePage.css';

interface InvoiceEntry {
  id: number;
  number: string;
  supplier_id: number;
  supplier_name?: string;
  total_amount: number;
  entry_date: string;
  issue_date?: string;
}

/** Formata data no fuso local (evita deslocamento de um dia com toISOString). */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addMonthsToDateString(dateStr: string, months: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setMonth(date.getMonth() + months);
  return formatLocalDate(date);
}

function addDaysToDateString(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

// Componente de Dropdown Customizado
function CustomDropdown({ 
  label, 
  options, 
  value, 
  onChange, 
  icon: Icon,
  placeholder = "Selecione...",
  error
}: {
  label: string;
  options: Array<{ value: string | number; label: string }>;
  value: string | number | null;
  onChange: (value: string | number) => void;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  placeholder?: string;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    
    const updateMenuPosition = () => {
      if (menuRef.current && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const menu = menuRef.current;
        const viewportHeight = window.innerHeight;
        const menuHeight = 240;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        
        menu.style.position = 'fixed';
        menu.style.width = `${buttonRect.width}px`;
        menu.style.left = `${buttonRect.left}px`;
        
        if (spaceBelow < menuHeight && buttonRect.top > menuHeight) {
          menu.style.top = 'auto';
          menu.style.bottom = `${viewportHeight - buttonRect.top + 8}px`;
        } else {
          menu.style.top = `${buttonRect.bottom + 8}px`;
          menu.style.bottom = 'auto';
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(updateMenuPosition, 0);
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayValue = options.find(o => o.value === value)?.label || value || placeholder;

  return (
    <div className={`custom-dropdown-wrapper ${isOpen ? 'dropdown-open' : ''}`} ref={dropdownRef}>
      <label className="custom-dropdown-label">{label}</label>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`custom-dropdown-button ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}
      >
        <div className="custom-dropdown-button-content">
          <Icon size={16} className={`custom-dropdown-icon ${isOpen ? 'active' : ''}`} />
          <span className={`custom-dropdown-text ${value ? 'has-value' : ''}`}>
            {displayValue}
          </span>
        </div>
        <ChevronDown size={14} className={`custom-dropdown-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="custom-dropdown-menu" ref={menuRef}>
          <div className="custom-dropdown-menu-content">
            {options.map((opt) => {
              const isActive = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`custom-dropdown-option ${isActive ? 'active' : ''}`}
                >
                  <span className="custom-dropdown-option-label">{opt.label}</span>
                  {isActive && <Check size={14} className="custom-dropdown-option-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

const IncomingAccountsPayablePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [invoiceEntry, setInvoiceEntry] = useState<InvoiceEntry | null>(null);
  
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    invoiceNumber: '',
    categoryId: '',
    subcategoryId: '',
    costCenterId: '',
    financialAccountId: '',
    installmentType: 'single' as 'single' | 'installment' | 'recurring_fixed' | 'recurring_variable',
    numberOfInstallments: 1,
    firstDueDate: '',
    competenceDate: '',
    period: 'monthly' as 'monthly' | 'biweekly',
    competenceDateType: 'due' as 'due' | 'previous_due',
    markAsPaid: false,
    observations: ''
  });
  
  const [categorias, setCategorias] = useState<any[]>([]);
  const [centrosCusto, setCentrosCusto] = useState<any[]>([]);
  const [contasFinanceiras, setContasFinanceiras] = useState<any[]>([]);
  const [parcelas, setParcelas] = useState<Array<{ number: number; dueDate: string; amount: number }>>([]);
  const [errors, setErrors] = useState<any>({});

  const loadInvoiceEntry = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await incomingService.getById(parseInt(id));
      if (response.success && response.data) {
        const invoice = response.data;
        setInvoiceEntry(invoice);

        // Pré-preencher formulário (vencimento vazio até o usuário informar competência)
        setFormData({
          description: `Conta ref. a NF ${invoice.number || invoice.id}`,
          amount: invoice.total_amount?.toString() || '0',
          invoiceNumber: invoice.number || '',
          categoryId: '',
          subcategoryId: '',
          costCenterId: '',
          financialAccountId: '',
          installmentType: 'single',
          numberOfInstallments: 1,
          firstDueDate: '',
          competenceDate: '',
          period: 'monthly',
          competenceDateType: 'due',
          markAsPaid: false,
          observations: `Conta a pagar referente à nota fiscal ${invoice.number || invoice.id}`
        });
      } else {
        showToast('Erro ao carregar dados da nota fiscal', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao carregar nota fiscal:', error);
      showToast(error.response?.data?.message || 'Erro ao carregar dados da nota fiscal', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const optionsRes = await incomingService.getFinancialOptions();
      if (optionsRes.success && optionsRes.data) {
        setCategorias(optionsRes.data.categories || []);
        setCentrosCusto(optionsRes.data.costCenters || []);
        setContasFinanceiras(optionsRes.data.accounts || []);
        return;
      }

      showToast('Não foi possível carregar categorias e contas para o lançamento.', 'error');
    } catch (error: any) {
      console.error('Erro ao carregar opções:', error);
      showToast(
        error?.response?.data?.message || 'Erro ao carregar categorias e contas para lançamento',
        'error'
      );
    } finally {
      setLoadingOptions(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (id) {
      loadInvoiceEntry();
      loadOptions();
    }
  }, [id, loadInvoiceEntry, loadOptions]);

  const generateInstallments = useCallback(() => {
    if (!formData.firstDueDate) {
      setParcelas([]);
      return;
    }

    const numParcelas = parseInt(formData.numberOfInstallments.toString()) || 1;
    const valorTotal = parseFloat(formData.amount) || 0;
    const valorParcela = valorTotal / numParcelas;

    const novasParcelas = [];
    for (let i = 0; i < numParcelas; i++) {
      novasParcelas.push({
        number: i + 1,
        dueDate: addMonthsToDateString(formData.firstDueDate, i),
        amount: i === numParcelas - 1
          ? valorTotal - (valorParcela * (numParcelas - 1))
          : parseFloat(valorParcela.toFixed(2))
      });
    }

    setParcelas(novasParcelas);
  }, [formData.amount, formData.firstDueDate, formData.numberOfInstallments]);

  const generateRecurringInstallments = useCallback(() => {
    if (!formData.firstDueDate) {
      setParcelas([]);
      return;
    }

    const valorTotal = parseFloat(formData.amount) || 0;
    const periodDays = formData.period === 'monthly' ? 30 : 15;

    const novasParcelas = [];
    for (let i = 0; i < 12; i++) {
      novasParcelas.push({
        number: i + 1,
        dueDate: addDaysToDateString(formData.firstDueDate, periodDays * i),
        amount: formData.installmentType === 'recurring_fixed'
          ? valorTotal
          : 0
      });
    }

    setParcelas(novasParcelas);
  }, [formData.amount, formData.firstDueDate, formData.installmentType, formData.period]);

  const handleCompetenceDateChange = (competenceDate: string) => {
    setFormData((prev) => ({
      ...prev,
      competenceDate,
      // Ao informar competência, sugere o mesmo vencimento (usuário pode alterar depois)
      firstDueDate: competenceDate || prev.firstDueDate
    }));
  };

  useEffect(() => {
    if (formData.installmentType === 'installment' && formData.numberOfInstallments > 0 && formData.firstDueDate) {
      generateInstallments();
    } else if (formData.installmentType === 'single' && formData.firstDueDate) {
      setParcelas([{
        number: 1,
        dueDate: formData.firstDueDate,
        amount: parseFloat(formData.amount) || 0
      }]);
    } else if ((formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && formData.firstDueDate) {
      generateRecurringInstallments();
    } else {
      setParcelas([]);
    }
  }, [
    formData.amount,
    formData.firstDueDate,
    formData.installmentType,
    formData.numberOfInstallments,
    formData.period,
    generateInstallments,
    generateRecurringInstallments
  ]);

  const updateParcelaAmount = (index: number, newAmount: string) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index].amount = parseFloat(newAmount) || 0;
    setParcelas(novasParcelas);
  };

  const updateParcelaDate = (index: number, newDate: string) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index].dueDate = newDate;
    setParcelas(novasParcelas);
  };

  const getObservationText = (parcela: { number: number; dueDate: string; amount: number }) => {
    let obs = formData.description;
    if (formData.invoiceNumber) {
      obs += ` - NF: ${formData.invoiceNumber}`;
    }
    if (formData.installmentType === 'installment') {
      obs += ` - Parcela ${parcela.number}/${parcelas.length}`;
    } else if (formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') {
      obs += ` - ${parcela.number}º período`;
    }
    if (formData.observations) {
      obs += ` | ${formData.observations}`;
    }
    return obs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!formData.description) {
      setErrors({ description: 'Descrição é obrigatória' });
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setErrors({ amount: 'Valor deve ser maior que zero' });
      return;
    }

    if (formData.installmentType === 'single' && !formData.firstDueDate) {
      setErrors({ firstDueDate: 'Data de vencimento é obrigatória' });
      return;
    }

    if (formData.installmentType === 'installment' && parcelas.length === 0) {
      setErrors({ installments: 'Gere as parcelas antes de salvar' });
      return;
    }

    if ((formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && parcelas.length === 0) {
      setErrors({ installments: 'Gere as parcelas antes de salvar' });
      return;
    }

    setIsProcessing(true);

    try {
      const contasParaCriar = parcelas.map(parcela => {
        const referenceData = {
          installmentType: formData.installmentType,
          installmentNumber: formData.installmentType === 'installment' ? parcela.number : null,
          totalInstallments: formData.installmentType === 'installment' ? parcelas.length : null,
          period: (formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') ? formData.period : null
        };

        let competenceDate = formData.competenceDate || null;
        if (!competenceDate) {
          if (formData.competenceDateType === 'due') {
            competenceDate = parcela.dueDate;
          } else if (formData.competenceDateType === 'previous_due') {
            competenceDate = addMonthsToDateString(parcela.dueDate, -1);
          }
        }

        const accountData: any = {
          supplierId: invoiceEntry?.supplier_id || null,
          amount: parcela.amount,
          dueDate: parcela.dueDate,
          competenceDate: competenceDate,
          status: formData.markAsPaid ? 'paid' : 'pending',
          categoryId: formData.subcategoryId || formData.categoryId || null,
          costCenterId: formData.costCenterId || null,
          financialAccountId: formData.financialAccountId || null,
          observation: getObservationText(parcela),
          referenceType: formData.installmentType !== 'single' ? 'installment' : 'invoice_entry',
          referenceId: formData.installmentType !== 'single' ? JSON.stringify(referenceData) : invoiceEntry?.id?.toString() || null,
          paymentDate: formData.markAsPaid ? parcela.dueDate : null
        };

        return accountData;
      });

      const result = await incomingService.createAccountsPayable(parseInt(id!, 10), {
        accounts: contasParaCriar
      });

      if (!result.success) {
        const partialErrors = result.errors?.length
          ? result.errors.join(', ')
          : result.message;
        setErrors({ submit: partialErrors || 'Erro ao criar contas a pagar' });
        return;
      }

      if (result.data?.errors?.length) {
        showToast(
          `Contas criadas com avisos: ${result.data.errors.join(', ')}`,
          'warning'
        );
      }

      // Atualizar status da nota fiscal para 'priced' (novo fluxo: contas antes de precificação)
      try {
        await incomingService.updateStatus(parseInt(id!), 'priced');
      } catch (statusError) {
        console.error('Erro ao atualizar status da nota:', statusError);
      }

      showToast('Conta(s) a pagar criada(s) com sucesso!', 'success');
      // Sempre redirecionar para o dashboard após criar a conta a pagar
      navigate('/erp/stock/incoming');
    } catch (error: any) {
      console.error('Erro ao criar contas:', error);
      setErrors({ submit: error.response?.data?.message || 'Erro ao criar contas a pagar' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Separar categorias principais (sem parent) e subcategorias
  const categoriasPrincipais = categorias.filter(c => !c.parent_id);
  const subcategorias = categorias.filter(c => c.parent_id);
  
  const categoryOptions = [
    { value: '', label: 'Selecione uma categoria' },
    ...categoriasPrincipais.map(c => ({ value: c.id.toString(), label: c.name }))
  ];

  const subcategoryOptions = [
    { value: '', label: 'Selecione uma subcategoria' },
    ...subcategorias
      .filter(sc => sc.parent_id === parseInt(formData.categoryId))
      .map(sc => ({ value: sc.id.toString(), label: sc.name }))
  ];

  const costCenterOptions = [
    { value: '', label: 'Selecione um centro de custo' },
    ...centrosCusto.map(c => ({ value: c.id.toString(), label: c.name }))
  ];

  const financialAccountOptions = [
    { value: '', label: 'Selecione uma conta bancária' },
    ...contasFinanceiras.map(c => ({ value: c.id.toString(), label: `${c.name} (${c.code})` }))
  ];

  const competencyRuleOptions = [
    { value: 'due', label: 'Mesma data de vencimento' },
    { value: 'previous_due', label: 'Mês anterior ao vencimento' }
  ];

  const periodOptions = [
    { value: 'monthly', label: 'Mensal' },
    { value: 'biweekly', label: 'Quinzenal' }
  ];

  if (loading || loadingOptions) {
    return (
      <div className="incoming-accounts-payable-loading">
        <Loader2 size={32} className="incoming-accounts-payable-spinner" />
        <p>Carregando dados da nota fiscal...</p>
      </div>
    );
  }

  return (
    <div className="incoming-accounts-payable-page">
      <Toast toast={toast} hideToast={hideToast} />
      
      {/* Header */}
      <div className="incoming-accounts-payable-header">
        <div className="incoming-accounts-payable-header-left">
          <button 
            onClick={() => navigate('/erp/stock/incoming')} 
            className="incoming-accounts-payable-back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="incoming-accounts-payable-title">
              Lançar <span className="incoming-accounts-payable-title-accent">Conta a Pagar</span>
            </h1>
            <p className="incoming-accounts-payable-subtitle">
              Nota Fiscal: {invoiceEntry?.number || `#${id}`}
            </p>
          </div>
        </div>
        <div className="incoming-accounts-payable-header-actions">
          <button 
            type="button"
            onClick={() => navigate('/erp/stock/incoming')}
            className="incoming-accounts-payable-cancel-button"
          >
            Cancelar
          </button>
          <button 
            onClick={handleSubmit}
            disabled={isProcessing || !formData.amount || parseFloat(formData.amount) <= 0}
            className="incoming-accounts-payable-save-button"
          >
            {isProcessing ? <Loader2 className="incoming-accounts-payable-save-spinner" size={18} /> : <Save size={18} />}
            Efetivar Lançamento
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="incoming-accounts-payable-form">
        {/* Coluna Principal */}
        <div className="incoming-accounts-payable-main-column">
          {/* Bloco 1: Identificação */}
          <div className="incoming-accounts-payable-card incoming-accounts-payable-card-identification">
            <h3 className="incoming-accounts-payable-card-title">
              <Briefcase className="incoming-accounts-payable-card-title-icon" size={16} />
              Identificação e Origem
            </h3>

            <div className="incoming-accounts-payable-card-content">
              <div className="incoming-accounts-payable-form-grid">
                <div className="incoming-accounts-payable-input-wrapper">
                  <label className="incoming-accounts-payable-input-label">Fornecedor</label>
                  <div className="incoming-accounts-payable-search-input-wrapper">
                    <Building2 size={16} className="incoming-accounts-payable-search-icon" />
                    <input 
                      type="text" 
                      className="incoming-accounts-payable-search-input"
                      value={invoiceEntry?.supplier_name || 'N/A'}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                <div className="incoming-accounts-payable-input-wrapper">
                  <label className="incoming-accounts-payable-input-label">Nº Faturamento / NF</label>
                  <div className="incoming-accounts-payable-search-input-wrapper">
                    <FileText size={16} className="incoming-accounts-payable-search-icon" />
                    <input 
                      type="text" 
                      className="incoming-accounts-payable-search-input"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                    />
                  </div>
                </div>

                <div className="incoming-accounts-payable-input-wrapper">
                  <label className="incoming-accounts-payable-input-label">Descrição do Lançamento</label>
                  <input 
                    type="text" 
                    required
                    className="incoming-accounts-payable-input"
                    placeholder="Ex: Compra de luvas, Aluguel..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                  {errors.description && (
                    <span className="error-message">{errors.description}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bloco 2: Classificação e Temporalidade */}
          <div className="incoming-accounts-payable-cards-grid">
            <div className="incoming-accounts-payable-card">
              <h3 className="incoming-accounts-payable-card-title">
                <Layers className="incoming-accounts-payable-card-title-icon" size={14} />
                Classificação
              </h3>
              <div className="incoming-accounts-payable-card-content">
                <CustomDropdown 
                  label="Categoria Principal"
                  options={categoryOptions}
                  value={formData.categoryId}
                  onChange={(val) => setFormData({...formData, categoryId: val.toString(), subcategoryId: ''})}
                  icon={Layers}
                  error={errors.categoryId}
                />
                
                {formData.categoryId && subcategoryOptions.length > 1 && (
                  <CustomDropdown 
                    label="Subcategoria"
                    options={subcategoryOptions}
                    value={formData.subcategoryId}
                    onChange={(val) => setFormData({...formData, subcategoryId: val.toString()})}
                    icon={ListTree}
                    error={errors.subcategoryId}
                  />
                )}
                
                <CustomDropdown 
                  label="Centro de Custo"
                  options={costCenterOptions}
                  value={formData.costCenterId}
                  onChange={(val) => setFormData({...formData, costCenterId: val.toString()})}
                  icon={Target}
                  error={errors.costCenterId}
                />
              </div>
            </div>

            <div className="incoming-accounts-payable-card">
              <h3 className="incoming-accounts-payable-card-title">
                <HelpCircle className="incoming-accounts-payable-card-title-icon" size={14} />
                Temporalidade (DRE)
              </h3>
              <div className="incoming-accounts-payable-card-content">
                <div className="incoming-accounts-payable-input-wrapper">
                  <label className="incoming-accounts-payable-input-label">Data de Competência</label>
                  <input 
                    type="date" 
                    className="incoming-accounts-payable-input"
                    value={formData.competenceDate}
                    onChange={(e) => handleCompetenceDateChange(e.target.value)}
                  />
                </div>
                
                <CustomDropdown 
                  label="Regra de Competência"
                  options={competencyRuleOptions}
                  value={formData.competenceDateType}
                  onChange={(val) => setFormData({...formData, competenceDateType: val as 'due' | 'previous_due'})}
                  icon={HelpCircle}
                />

                <CustomDropdown 
                  label="Conta Bancária de Saída"
                  options={financialAccountOptions}
                  value={formData.financialAccountId}
                  onChange={(val) => setFormData({...formData, financialAccountId: val.toString()})}
                  icon={Wallet}
                  error={errors.financialAccountId}
                />
              </div>
            </div>
          </div>

          {/* Bloco 3: Observações */}
          <div className="incoming-accounts-payable-card">
            <label className="incoming-accounts-payable-input-label">Observações Internas (Opcional)</label>
            <textarea 
              rows={4} 
              className="incoming-accounts-payable-textarea"
              placeholder="Detalhes adicionais relevantes para auditoria financeira..."
              value={formData.observations}
              onChange={(e) => setFormData({...formData, observations: e.target.value})}
            />
          </div>
        </div>

        {/* Coluna Lateral: Financeiro */}
        <div className="incoming-accounts-payable-sidebar">
          <div className="incoming-accounts-payable-sidebar-card">
            <h3 className="incoming-accounts-payable-sidebar-title">
              <Wallet className="incoming-accounts-payable-sidebar-title-icon" size={16} />
              Definições Financeiras
            </h3>

            <div className="incoming-accounts-payable-sidebar-content">
              <div className="incoming-accounts-payable-amount-wrapper">
                <label className="incoming-accounts-payable-amount-label">Valor Total</label>
                <div className="incoming-accounts-payable-amount-input-wrapper">
                  <span className="incoming-accounts-payable-amount-currency">R$</span>
                  <input 
                    type="number" 
                    step="0.01"
                    required
                    className="incoming-accounts-payable-amount-input"
                    placeholder="0,00"
                    value={formData.amount || ''}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  />
                </div>
                {errors.amount && (
                  <span className="error-message">{errors.amount}</span>
                )}
              </div>

              <div className="incoming-accounts-payable-payment-type">
                <label className="incoming-accounts-payable-payment-type-label">Tipo de Saída</label>
                <div className="incoming-accounts-payable-payment-type-grid">
                  {[
                    { id: 'single', label: 'Única', icon: Receipt },
                    { id: 'installment', label: 'Parcelada', icon: ListOrdered },
                    { id: 'recurring_fixed', label: 'Fixo Recorr.', icon: Repeat },
                    { id: 'recurring_variable', label: 'Var. Recorr.', icon: TrendingUp }
                  ].map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ 
                        ...formData, 
                        installmentType: type.id as any,
                        numberOfInstallments: type.id === 'installment' ? 2 : 1
                      })}
                      className={`incoming-accounts-payable-payment-type-button ${formData.installmentType === type.id ? 'active' : ''}`}
                    >
                      <type.icon size={16} />
                      <span className="incoming-accounts-payable-payment-type-button-label">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {formData.installmentType === 'installment' && (
                <div className="incoming-accounts-payable-installment-fields">
                  <div className="incoming-accounts-payable-installment-field">
                    <label className="incoming-accounts-payable-installment-label">Parcelas</label>
                    <input 
                      type="number" 
                      min="1"
                      className="incoming-accounts-payable-installment-input"
                      value={formData.numberOfInstallments}
                      onChange={(e) => setFormData({...formData, numberOfInstallments: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div className="incoming-accounts-payable-installment-field">
                    <label className="incoming-accounts-payable-installment-label">1º Venc.</label>
                    <input 
                      type="date" 
                      className="incoming-accounts-payable-installment-input"
                      value={formData.firstDueDate}
                      onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                    />
                    {errors.firstDueDate && (
                      <span className="error-message">{errors.firstDueDate}</span>
                    )}
                  </div>
                </div>
              )}

              {(formData.installmentType === 'recurring_fixed' || formData.installmentType === 'recurring_variable') && (
                <div className="incoming-accounts-payable-recurring-fields">
                  <div className="incoming-accounts-payable-installment-fields">
                    <div className="incoming-accounts-payable-installment-field">
                      <label className="incoming-accounts-payable-installment-label">Período</label>
                      <select
                        className="incoming-accounts-payable-installment-input"
                        value={formData.period}
                        onChange={(e) => setFormData({...formData, period: e.target.value as 'monthly' | 'biweekly'})}
                      >
                        {periodOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="incoming-accounts-payable-installment-field">
                      <label className="incoming-accounts-payable-installment-label">1º Venc.</label>
                      <input 
                        type="date" 
                        className="incoming-accounts-payable-installment-input"
                        value={formData.firstDueDate}
                        onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                      />
                      {errors.firstDueDate && (
                        <span className="error-message">{errors.firstDueDate}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {formData.installmentType === 'single' && (
                <div className="incoming-accounts-payable-installment-fields">
                  <div className="incoming-accounts-payable-installment-field">
                    <label className="incoming-accounts-payable-installment-label">Vencimento</label>
                    <input 
                      type="date" 
                      className="incoming-accounts-payable-installment-input"
                      value={formData.firstDueDate}
                      onChange={(e) => setFormData({...formData, firstDueDate: e.target.value})}
                    />
                    {errors.firstDueDate && (
                      <span className="error-message">{errors.firstDueDate}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Lista de Parcelas */}
              {parcelas.length > 0 && (
                <div className="incoming-accounts-payable-installments-list">
                  <p className="incoming-accounts-payable-installments-list-title">
                    Projeção da Grade
                  </p>
                  <div className="incoming-accounts-payable-installments-list-content">
                    {parcelas.map((parcela, idx) => (
                      <div key={idx} className="incoming-accounts-payable-installment-item">
                        <span className="incoming-accounts-payable-installment-item-number">
                          {parcela.number}/{parcelas.length}
                        </span>
                        <div className="incoming-accounts-payable-installment-item-content">
                          <input 
                            type="date" 
                            className="incoming-accounts-payable-installment-item-date"
                            value={parcela.dueDate}
                            onChange={(e) => updateParcelaDate(idx, e.target.value)}
                          />
                          <div className="incoming-accounts-payable-installment-item-amount-wrapper">
                            <span className="incoming-accounts-payable-installment-item-amount-currency">R$</span>
                            <input 
                              type="number"
                              step="0.01"
                              className="incoming-accounts-payable-installment-item-amount"
                              value={parcela.amount.toFixed(2)}
                              onChange={(e) => updateParcelaAmount(idx, e.target.value)}
                              disabled={formData.installmentType === 'recurring_fixed'}
                            />
                          </div>
                        </div>
                        <div className="incoming-accounts-payable-installment-item-badge">
                          <span className="incoming-accounts-payable-installment-item-badge-text">Provisão</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="incoming-accounts-payable-info-box">
                <Info size={16} className="incoming-accounts-payable-info-icon" />
                <p className="incoming-accounts-payable-info-text">
                  Os títulos serão baixados automaticamente após o <strong>vínculo com o extrato bancário</strong> na tela de conciliação.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      {errors.submit && (
        <div className="incoming-accounts-payable-error-message">{errors.submit}</div>
      )}
    </div>
  );
};

export default IncomingAccountsPayablePage;
